import * as React from 'react';
import { Spinner } from '@vechaiui/react';
import { useLocalization } from '@fluent/react';
import { useQuery } from '@apollo/client';
import { useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

import MainLayout from '#/layouts/MainLayout';
import OrderDetailsPanel from '#/components/panels/OrderDetailsPanel';
import OrderListPanel from '#/components/panels/OrderListPanel';
import { GetOrdersDocument, OrderStatus } from '#/__generated__/graphql/graphql';
import { SearchParam } from '#/constants/search-params';
import { normalizeOrderStatus } from '#/utils/order-status';
import { useInfiniteList } from '#/hooks/use-infinite-list';

const OrderPage: React.FC<OrderPageProps> = () => {
  const rootRef = useRef<HTMLDivElement>(null);
  const elementRef = useRef<HTMLDivElement>(null);
  const { l10n } = useLocalization();
  const [searchParams] = useSearchParams();
  const keywords = searchParams.get(SearchParam.Keywords);
  const orderNumber = searchParams.get(SearchParam.OrderNumber);
  const orderStatuses = composeOrderStatusFilter(searchParams.get(SearchParam.Statuses));

  const { data, fetchMore, loading } = useQuery(GetOrdersDocument, {
    // FIXME: how to use cache properly?
    fetchPolicy: 'no-cache',
    variables: { filter: { orderStatuses, keywords } },
  });

  useInfiniteList(elementRef, rootRef, () => {
    if (!loading && data?.orders?.pageInfo.hasNextPage) {
      fetchMore({ variables: { cursor: data?.orders?.pageInfo?.endCursor } });
    }
  });

  return (
    <MainLayout
      leftPage={
        data?.orders && (
          <div className="p-6 2xl:px-8 2xl:py-12">
            <OrderListPanel orders={data.orders.edges.map((o) => o.node)} />
          </div>
        )
      }
      ref={rootRef}
    >
      <React.Suspense
        fallback={
          <div className="flex items-center justify-center h-full">
            <Spinner size="xl" />
          </div>
        }
      >
        {orderNumber && <OrderDetailsPanel orderNumber={orderNumber} />}
        {!orderNumber && (
          // TODO - Create a no order selected view
          <div className="text-center bg-gray-100 pt-60">{l10n.getString('no-order-selected')}</div>
        )}

        <div ref={elementRef} />
      </React.Suspense>
    </MainLayout>
  );
};
export type OrderPageProps = {};

export function composeOrderStatusFilter(statuses?: string | null) {
  if (!statuses) {
    return Object.values(OrderStatus);
  }

  return statuses.split(',').map(normalizeOrderStatus);
}

export default OrderPage;
